import { AnalyticsStore, RouteStore } from 'bernie-core';
import { KeyedObject } from 'bernie-core/dist/source';
import { ActivityDestinationViewSearch } from '../common/__generated__/api';
import { buildMicroMessage } from '../mappers/analytics-store';
import { ActivitySearchStore, FILTER_URL_PARAM_PREFIX, SearchCriteriaStore } from '../stores';
import { getFilteredObjectKeys } from '../helpers/common-helper';
import { ActivityDetailBasic } from '../common/__generated__/api/types';
import { refreshAds } from './../utils/refresh-ads';

const REGION_HOTEL = 'hotel';

export function trackSrpTileClick(
  activity: ActivityDestinationViewSearch.ActivityInlineFragment,
  index: number,
  analytics: AnalyticsStore
): void {
  const allActivityProducts = buildMicroMessage('allActivityProducts', {
    numOfProducts: 1,
    activityProducts: [{ productId: parseInt(activity.id, 10), sortPosition: index + 1 }]
  });
  if (activity.analytics) {
    analytics.trackEvent(activity.analytics.referrerId, 'RFRR Action Link', analytics.omnitureData, null, true, [
      allActivityProducts
    ]);
  }
}

export function updateSearchQueryString(searchCriteriaStore: SearchCriteriaStore, routeStore: RouteStore): void {
  if (searchCriteriaStore && !searchCriteriaStore.isQueryStringUpdated) {
    searchCriteriaStore.isQueryStringUpdated = true;
    const searchQuery = searchCriteriaStore.searchQuery;
    if (searchQuery && searchCriteriaStore.isQueryKeysDifferent(routeStore.queryParams)) {
      routeStore.updateSearch(searchQuery, {}, true);
    }
  }
}

function getRegionDetails(activitySearchStore: ActivitySearchStore): ActivityDetailBasic.Region {
  const regionType = (activitySearchStore.query && activitySearchStore.query.regionType === REGION_HOTEL) || null;
  const resolvedRegion = activitySearchStore.searchSummary && activitySearchStore.searchSummary.resolvedRegion;
  const queryLocation = activitySearchStore.query ? activitySearchStore.query.location : '';

  return {
    regionName: (regionType && queryLocation) || (resolvedRegion && resolvedRegion.fullRegionName) || queryLocation,
    regionId: (regionType && activitySearchStore.query.rid) || (resolvedRegion && resolvedRegion.regionId) || ''
  };
}

export function updateSearchCriteriaStore(
  searchCriteriaStore: SearchCriteriaStore,
  activitySearchStore: ActivitySearchStore,
  query: KeyedObject
): void {
  const searchSummary = activitySearchStore.searchSummary;
  const searchedDates = searchSummary && searchSummary.dateValidationResult;

  const searchResponseStartDate = searchedDates && searchedDates.searchedStartDate && searchedDates.searchedStartDate;

  const searchResponseEndDate = searchedDates && searchedDates.searchedEndDate && searchedDates.searchedEndDate;

  const swpApplied = query.swp !== 'off';

  const isoStartDate = (searchResponseStartDate && searchResponseStartDate.shortDateFormat) || '';
  const isoEndDate = (searchResponseEndDate && searchResponseEndDate.shortDateFormat) || '';

  const { regionName, regionId } = getRegionDetails(activitySearchStore);
  const filterKeys = getFilteredObjectKeys(query, FILTER_URL_PARAM_PREFIX);
  const filterParams: KeyedObject = {};
  filterKeys.forEach(filterKey => {
    filterParams[filterKey] = query[filterKey];
  });

  searchCriteriaStore.searchQuery = {
    ...filterParams,
    location: regionName || '',
    sort: query.sort,
    slimSearchKeyword: query.slimSearchKeyword,
    swp: swpApplied ? 'on' : 'off',
    startDate: isoStartDate,
    endDate: isoEndDate,
    rid: regionId,
    selectedId: query.selectedId,
    mctc: query.mctc,
    travelDurationFromHotelEnabled: query.travelDurationFromHotelEnabled,
    regionType: query.regionType,
    latLong: query.latLong,
    landingPage: query.landingPage
  };
}

export function handlePillsClick(
  routeStore: RouteStore,
  selections: ActivityDestinationViewSearch.Selections[],
  pageId: string,
  analytics: AnalyticsStore
): void {
  if (selections.length > 0) {
    const filterCategory = selections[0].id;
    if (filterCategory === 'slimSearchKeyword') {
      const slimSearchKeyword: string = (routeStore.queryParams && routeStore.queryParams.slimSearchKeyword) || '';

      if (slimSearchKeyword === selections[0].value) {
        analytics.trackEvent(
          `LX.SR.Remove.Keyword.${selections[0].value}`,
          'keyword-pill-removed',
          analytics.omnitureData
        );
        routeStore.updateSearch({ slimSearchKeyword: '' });
        refreshAds();
      }
    } else {
      const filterCategoryKey = FILTER_URL_PARAM_PREFIX + filterCategory;
      const oldFiltersString = (routeStore.queryParams && routeStore.queryParams[filterCategoryKey]) || '';

      const filters = oldFiltersString.split(',').filter((filter: string) => filter !== '');
      selections.forEach(selection => {
        const indexOfFilterToDelete = filters.indexOf(selection.value);
        if (indexOfFilterToDelete > -1) {
          filters.splice(indexOfFilterToDelete, 1);
        }
      });
      const filtersString = filters.join(',');

      if (filterCategory === 'seeAll') {
        analytics.trackEvent('LX.SR.Remove.SeeAll', 'see-all-pill-removed', analytics.omnitureData);
        if (routeStore.queryParams && routeStore.queryParams.regionType) {
          routeStore.updateSearch({
            [filterCategoryKey]: filtersString,
            sort: 'RECOMMENDED',
            selectedId: '',
            regionType: ''
          });
        } else {
          routeStore.updateSearch({
            [filterCategoryKey]: filtersString,
            sort: 'RECOMMENDED',
            selectedId: ''
          });
        }
        refreshAds();
      } else {
        analytics.trackEvent(
          `LX.${pageId}.Remove.${filterCategory}.${selections[0].value}`,
          `${filterCategory}-pill-removed`,
          analytics.omnitureData
        );
        routeStore.updateSearch({ [filterCategoryKey]: filtersString });
        refreshAds();
      }
    }
  }
}

export function getBreadcrumbUrl(
  routeStore: RouteStore,
  selectionsToRemove: ActivityDestinationViewSearch.SelectionsToRemove[]
): string {
  let searchQueryString = routeStore.search;
  const key = FILTER_URL_PARAM_PREFIX + selectionsToRemove[0].id;
  const existingUrlFilterValues =
    routeStore.queryParams && routeStore.queryParams[key] ? routeStore.queryParams[key].split(',') : [];

  if (existingUrlFilterValues.length > 0) {
    selectionsToRemove.forEach((selection: ActivityDestinationViewSearch.SelectionsToRemove) => {
      const indexOfUrlFilterToDelete = existingUrlFilterValues.indexOf(selection.value);
      if (indexOfUrlFilterToDelete > -1) {
        existingUrlFilterValues.splice(indexOfUrlFilterToDelete, 1);
      }
    });
    const updatedUrlFilterValues =
      existingUrlFilterValues.length > 0 ? { [key]: existingUrlFilterValues.join(',') } : {};
    searchQueryString = routeStore.mergeQueryString(routeStore.search, updatedUrlFilterValues, queryKey => {
      if (queryKey === key) {
        return Boolean(updatedUrlFilterValues[queryKey]);
      }

      return Boolean(routeStore.queryParams[queryKey]);
    });
  }

  return `${routeStore.pathname}?${searchQueryString}`;
}
